import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AdminLayout from '@/layouts/AdminLayout';
import { useAuthState } from '@/hooks/useAuthState';
import { PropertyService } from '@/services/propertyService';
import {
  Home,
  Search,
  Plus,
  Edit,
  Trash2,
  Eye,
  ArrowLeft,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  BadgeCheck
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Property {
  id: number;
  title: string;
  address: string;
  city: string;
  state: string;
  property_type: string;
  owner_id: number;
  owner_name?: string;
  development_name?: string | null;
  created_at: string;
  is_active: boolean;
  is_verified?: boolean;
  cover_image?: string;
}

const AdminProperties = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userType, userName, userPhoto, userId, loading: authLoading } = useAuthState();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Estado para o input
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [filterStatus, setFilterStatus] = useState('todos');
  const [filterType, setFilterType] = useState('todos');

  // Verificar se é admin (AdminLayout já faz isso)

  useEffect(() => {
    loadProperties();
  }, [currentPage, itemsPerPage, searchTerm, filterStatus, filterType]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      
      // Obter o total real de propriedades no banco (sem limite)
      const totalCount = await PropertyService.getTotalPropertiesCount(true);
      
      // Carregar propriedades do banco de dados (incluindo inativas)
      // Usar um limite alto para carregar todas as propriedades, mas ainda aplicar paginação no frontend
      const allProperties = await PropertyService.getAllPropertiesForAdmin(10000, 0);
      console.log('🏠 Propriedades carregadas:', allProperties);

      // Aplicar filtros
      let filteredProperties = allProperties;
      
      // Filtro por termo de busca
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase().trim();
        filteredProperties = filteredProperties.filter(property => {
          // Buscar em múltiplos campos (tratando valores nulos/undefined)
          const title = (property.title || '').toLowerCase();
          const city = (property.city || '').toLowerCase();
          const address = (property.address || '').toLowerCase();
          const development = (property.development_name || '').toLowerCase();
          const owner = (property.owner_name || '').toLowerCase();
          const propertyId = String(property.id || '');
          
          // Verificar se o termo de busca corresponde a algum campo
          return title.includes(searchLower) ||
                 city.includes(searchLower) ||
                 address.includes(searchLower) ||
                 development.includes(searchLower) ||
                 owner.includes(searchLower) ||
                 propertyId.includes(searchLower);
        });
      }
      
      // Filtro por status
      if (filterStatus !== 'todos') {
        filteredProperties = filteredProperties.filter(property => {
          if (filterStatus === 'ativo') {
            return Boolean(property.is_active) === true;
          } else if (filterStatus === 'inativo') {
            return Boolean(property.is_active) === false;
          }
          return true;
        });
      }
      
      // Filtro por tipo de imóvel
      if (filterType !== 'todos') {
        filteredProperties = filteredProperties.filter(property => 
          property.property_type === filterType
        );
      }

      // Calcular paginação
      // Se houver filtros aplicados, usar o tamanho do array filtrado
      // Caso contrário, usar o total real do banco (que conta todas as propriedades)
      let total: number;
      if (searchTerm || filterStatus !== 'todos' || filterType !== 'todos') {
        // Com filtros, contar as propriedades filtradas
        total = filteredProperties.length;
      } else {
        // Sem filtros, usar o total real do banco
        total = totalCount;
      }
      
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedProperties = filteredProperties.slice(startIndex, endIndex);

      console.log('📊 Propriedades filtradas:', filteredProperties);
      console.log('📄 Propriedades paginadas:', paginatedProperties);
      console.log('🔢 IDs das propriedades:', paginatedProperties.map(p => p.id));
      console.log('🔍 Filtros aplicados:', { searchTerm, filterStatus, filterType });
      setProperties(paginatedProperties);
      setTotalItems(total);
    } catch (error) {
      console.error('❌ Erro ao carregar imóveis:', error);
      // Em caso de erro, definir array vazio
      setProperties([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleFilterChange = (filterType: 'status' | 'type', value: string) => {
    if (filterType === 'status') {
      setFilterStatus(value);
    } else {
      setFilterType(value);
    }
    setCurrentPage(1); // Reset para primeira página ao mudar filtros
  };

  const handleEditProperty = (propertyId: number) => {
    navigate(`/admin/imoveis/editar/${propertyId}`);
  };

  const handleTogglePropertyStatus = async (propertyId: number) => {
    const property = properties.find(p => p.id === propertyId);
    if (!property) return;
    
    const action = property.is_active ? 'desativar' : 'ativar';
    if (confirm(`Tem certeza que deseja ${action} este imóvel?`)) {
      try {
        console.log(`${action.charAt(0).toUpperCase() + action.slice(1)} imóvel:`, propertyId);
        
        // Atualizar status no banco de dados
        await PropertyService.updateProperty(propertyId, {
          is_active: !property.is_active
        });
        
        // Recarregar lista
        loadProperties();
      } catch (error) {
        console.error(`Erro ao ${action} imóvel:`, error);
      }
    }
  };

  const handleToggleVerification = async (propertyId: number, currentStatus: boolean) => {
    try {
      console.log('🔄 Alternando verificação do imóvel:', propertyId, 'para:', !currentStatus);
      
      // Atualizar status de verificação no banco de dados
      await PropertyService.updateProperty(propertyId, {
        is_verified: !currentStatus
      });
      
      // Recarregar lista
      loadProperties();
    } catch (error) {
      console.error('Erro ao alternar verificação do imóvel:', error);
    }
  };

  const handleViewProperty = (propertyId: number) => {
    console.log('🔍 Visualizando propriedade ID:', propertyId);
    navigate(`/imovel/${propertyId}`);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando imóveis...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Gerenciar Imóveis</h1>
                <p className="text-muted-foreground">
                  Cadastre, edite e gerencie imóveis do sistema
                </p>
    </div>
    </div>
    </div>

          {/* Busca */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        placeholder="Buscar por título, nome do proprietário, cidade, endereço ou empreendimento..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="pl-10"
                      />
                    </div>
                    <Button 
                      onClick={handleSearch}
                      variant="outline"
                      className="px-4"
                    >
                      <Search className="h-4 w-4" />
                    </Button>
                    {searchTerm && (
                      <Button 
                        onClick={handleClearSearch}
                        variant="outline"
                        className="px-4"
                      >
                        Limpar
                      </Button>
                    )}
                  </div>
                  {searchTerm && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Buscando por: <strong>"{searchTerm}"</strong>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  {/* Filtro por Status */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Status:</span>
                    <Select value={filterStatus} onValueChange={(value) => handleFilterChange('status', value)}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        <SelectItem value="ativo">Ativo</SelectItem>
                        <SelectItem value="inativo">Inativo</SelectItem>
                      </SelectContent>
                    </Select>
    </div>
                  
                  {/* Filtro por Tipo de Imóvel */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Tipo:</span>
                    <Select value={filterType} onValueChange={(value) => handleFilterChange('type', value)}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        <SelectItem value="apartment">Apartamento</SelectItem>
                        <SelectItem value="house">Casa</SelectItem>
                        <SelectItem value="studio">Studio</SelectItem>
                        <SelectItem value="loft">Loft</SelectItem>
                      </SelectContent>
                    </Select>
    </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Itens por página:</span>
                    <Select value={itemsPerPage.toString()} onValueChange={(value) => handleItemsPerPageChange(parseInt(value))}>
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
    </div>
    </div>
    </div>
            </CardContent>
          </Card>

          {/* Tabela de Imóveis */}
          <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Home className="h-5 w-5" />
            Imóveis ({totalItems} total)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Imóvel</TableHead>
                <TableHead>Empreendimento</TableHead>
                <TableHead>Proprietário</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Verificado</TableHead>
                <TableHead>Data de Cadastro</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      Carregando imóveis...
    </div>
                  </TableCell>
                </TableRow>
              ) : properties.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Nenhum imóvel encontrado
                  </TableCell>
                </TableRow>
              ) : (
                properties.map((property) => {
                  console.log('🏠 Renderizando propriedade:', property.id, property.title);
                  return (
                <TableRow key={property.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Home className="h-4 w-4 text-primary" />
    </div>
                      <div>
                        <div className="font-medium">{property.title}</div>
    </div>
    </div>
                  </TableCell>
                  <TableCell>
                    {property.development_name || '-'}
                  </TableCell>
                  <TableCell>{property.owner_name || `ID: ${property.owner_id}`}</TableCell>
                  <TableCell>
                    <Badge variant={property.is_active ? 'default' : 'secondary'}>
                      {property.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={property.is_verified || false}
                        onCheckedChange={() => handleToggleVerification(property.id, property.is_verified || false)}
                      />
                      {Number(property.is_verified) === 1 && (
                        <BadgeCheck className="h-4 w-4 text-blue-500" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {new Date(property.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewProperty(property.id)}>
                          <Eye className="h-4 w-4 mr-2" />
                          Visualizar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEditProperty(property.id)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleTogglePropertyStatus(property.id)}
                          className={property.is_active ? "text-red-600" : "text-green-600"}
                        >
                          {property.is_active ? (
                            <>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Desativar
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Ativar
                            </>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} registros
    </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                
                <div className="flex items-center gap-1">
                  {(() => {
                    const pages: (number | string)[] = [];
                    const maxVisible = 5; // Máximo de 5 botões visíveis
                    
                    if (totalPages <= maxVisible) {
                      // Se há 5 ou menos páginas, mostrar todas
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      // Paginação inteligente para muitas páginas
                      // Sempre mostrar primeira página
                      pages.push(1);
                      
                      // Calcular range de páginas ao redor da atual
                      let startPage = Math.max(2, currentPage - 2);
                      let endPage = Math.min(totalPages - 1, currentPage + 2);
                      
                      // Ajustar se estiver muito no início
                      if (currentPage <= 3) {
                        startPage = 2;
                        endPage = Math.min(5, totalPages - 1);
                      }
                      
                      // Ajustar se estiver muito no final
                      if (currentPage >= totalPages - 2) {
                        startPage = Math.max(2, totalPages - 4);
                        endPage = totalPages - 1;
                      }
                      
                      if (startPage > 2) {
                        pages.push('...');
                      }
                      
                      // Mostrar páginas ao redor da página atual
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(i);
                      }
                      
                      if (endPage < totalPages - 1) {
                        pages.push('...');
                      }
                      
                      // Sempre mostrar última página
                      pages.push(totalPages);
                    }
                    
                    return pages.map((page, index) => {
                      if (page === '...') {
                        return (
                          <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">
                            ...
                          </span>
                        );
                      }
                      
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(page as number)}
                          className="w-8 h-8 p-0"
                        >
                          {page}
                        </Button>
                      );
                    });
                  })()}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                  <ChevronRight className="h-4 w-4" />
                </Button>
    </div>
    </div>
          </CardContent>
        </Card>
      )}
    </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProperties;
